import { useEffect, useRef, useState } from "react";
import { getMe } from "../lib/api";

export function ShareModal({
  projectId,
  appName,
  slug,
  onClose,
}: {
  projectId: string;
  appName: string;
  /** The project's deploy slug, if it's been deployed. Enables the pretty share URL. */
  slug?: string | null;
  onClose: () => void;
}) {
  // Prefer the human-readable /<username>/<slug> URL when the project is deployed (has a
  // slug) and the owner has a username. Fall back to the canonical /a/<id> otherwise
  // (guest, or not-yet-deployed). We fetch the username from /api/me on open.
  const [username, setUsername] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    getMe()
      .then((me) => {
        if (!cancelled) setUsername(me.user?.username ?? null);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const url =
    username && slug
      ? `${location.origin}/${username}/${slug}`
      : `${location.origin}/a/${projectId}`;
  const qrSrc = `/a/${projectId}/qr.svg?size=512&url=${encodeURIComponent(url)}`;
  const [copied, setCopied] = useState(false);
  const [copyHint, setCopyHint] = useState<string | null>(null);
  const urlRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  async function doCopy() {
    function fallback() {
      // Highlight the URL so the user can tap-and-hold → Copy from the OS menu.
      // This is the iOS Safari / non-HTTPS / clipboard-permission-denied path.
      const el = urlRef.current;
      if (el) {
        const range = document.createRange();
        range.selectNodeContents(el);
        const sel = window.getSelection();
        sel?.removeAllRanges();
        sel?.addRange(range);
      }
      setCopyHint("select & copy");
      setTimeout(() => setCopyHint(null), 3000);
    }
    try {
      if (!navigator.clipboard?.writeText) {
        fallback();
        return;
      }
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      fallback();
    }
  }

  return (
    <div className="modal-bg" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal" style={{ width: 440, maxWidth: "96vw" }} role="dialog" aria-label="Share app">
        <h2 style={{ marginBottom: 4 }}>{appName || "Untitled app"}</h2>
        <div className="hint" style={{ marginBottom: 16, fontSize: 12 }}>
          Scan the QR or copy the link to share this app.
        </div>
        <div
          style={{
            background: "white",
            padding: 14,
            borderRadius: 10,
            display: "block",
            maxWidth: 320,
            margin: "0 auto",
          }}
        >
          <img src={qrSrc} alt="QR code" style={{ display: "block", width: "100%", height: "auto" }} />
        </div>
        <div
          style={{
            marginTop: 14,
            display: "flex",
            gap: 6,
            alignItems: "stretch",
            flexWrap: "wrap",
          }}
        >
          <div
            ref={urlRef}
            className="mono"
            style={{
              flex: "1 1 200px",
              minWidth: 0,
              background: "var(--code-bg)",
              border: "1px solid var(--border-2)",
              borderRadius: 8,
              padding: "10px 12px",
              fontSize: 12,
              // Wrap long URLs instead of "..." truncation — on a phone the user actually
              // wants to SEE the URL and tap-select it as a fallback if copy fails.
              wordBreak: "break-all",
              userSelect: "all",
              WebkitUserSelect: "all",
            }}
          >
            {url}
          </div>
          <button onClick={doCopy} style={{ minWidth: 80 }}>{copied ? "✓ copied" : "Copy"}</button>
        </div>
        {copyHint && (
          <div style={{ marginTop: 8, fontSize: 11, color: "var(--fg-faint)", textAlign: "center" }}>
            {copyHint}
          </div>
        )}
        <div className="actions" style={{ marginTop: 16 }}>
          <button onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}
